import { get, isEmpty } from 'lodash-es';
import { type ChangeEvent, useEffect, useRef } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';
import ConversationAttachmentMenu from '@/components/conversations/attachment-form/conversation-attachment-menu';
import useConversationForm from '@/hooks/conversations/use-conversation-form';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import usePickMediaFile from '@/hooks/files/use-pick-media-file';
import useSystemSetting from '@/hooks/files/use-system-setting';
import type { Uploadable } from '@/models/uploadable';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FILE_LIMIT_MAP, FILE_TYPE_MESSAGE_MAP, FileType } from '@/utils/file-type';
import { convertFileToUploadable } from '@/utils/uploadable';
import FileSelectorFlyout from './file-selector-flyout';

interface Props {
  open: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onClose?: () => void;
}

export function ConversationAttachmentForm({ open, disabled, onClose }: Props) {
  const { t } = useTranslation();
  const {
    form: { control, reset, formState, clearErrors, getValues },
    submit,
  } = useConversationForm();
  const { ref: fileInputRef, pickMedia, clearMedia } = usePickMediaFile();

  const { isAdminSession } = useConversationStore();
  const { getFileSizeLimit } = useSystemSetting();
  const { field: filesField } = useController({ control, name: 'Attachment' });
  const { field: fileTypeField } = useController({ control, name: 'AttachmentType' });
  const [files, fileType] = useWatch({ control, name: ['Attachment', 'AttachmentType'] });

  const fileLimit = fileType ? get(FILE_LIMIT_MAP, fileType) : 0;

  const attachmentFormRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(attachmentFormRef, () => {
    if (document.getElementById('chatroom-modal') || !open) return;
    onClose?.();
  });

  const clearFileInput = () => {
    clearMedia();
    reset();
  };

  const summonFileSelectDialog = (fileType: FileType, type?: 'camera' | 'album') => {
    fileTypeField.onChange(fileType);

    const hasQuote = getValues('Quote') !== undefined;
    const limit = hasQuote ? 1 : FILE_LIMIT_MAP[fileType];

    return pickMedia({
      accept: fileType,
      type,
      limit,
    });
  };

  const handleSelectFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles?.length) return;

    const uploadables: Uploadable[] = [];
    for (const file of selectedFiles) {
      const fileSizeLimit = getFileSizeLimit(file.type.split('/')[0], isAdminSession);
      if (file.size > fileSizeLimit * 1.05) {
        addToast({
          type: 'error',
          message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
        });
        if (fileType !== FileType.File || fileType === undefined) {
          uploadables.push(convertFileToUploadable(file));
        }
      } else {
        uploadables.push(convertFileToUploadable(file));
      }
    }

    if (!isEmpty(uploadables)) {
      filesField.onChange(uploadables.concat(files ?? []));
      e.target.value = '';

      if (fileType === FileType.File || fileType === undefined) {
        submit();
        clearFileInput();
      }
    }

    onClose?.();
  };

  useEffect(() => {
    if (formState.errors.Attachment && fileType) {
      addToast({
        type: 'error',
        message: t('errors.multi-file-select-limit', {
          unit: t(FILE_TYPE_MESSAGE_MAP[fileType], { count: fileLimit }),
        }),
      });
      clearErrors('Attachment');
    }
  }, [formState.errors.Attachment, fileLimit, fileType]);

  return (
    <>
      <ConversationAttachmentMenu
        ref={attachmentFormRef}
        disabled={disabled}
        isOpen={open}
        onPick={summonFileSelectDialog}
      />

      {fileType && fileType !== FileType.File && (
        <FileSelectorFlyout
          files={files ?? []}
          fileLimit={fileLimit}
          onPick={summonFileSelectDialog}
          onCancel={clearFileInput}
        />
      )}

      <input
        {...filesField}
        value={undefined}
        onChange={handleSelectFiles}
        type='file'
        ref={(r) => {
          filesField.ref(r);
          fileInputRef.current = r;
        }}
        className='hidden'
      />
    </>
  );
}
